import { ActivityFeed } from './Activity';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import { api } from '../api';
import type { SessionUser } from '../api';
import {
  ArchiveButton,
  ArchivedToggle,
  Breadcrumbs,
  Button,
  Card,
  Empty,
  ErrorBanner,
  Field,
  List,
  ListRow,
  Modal,
  ModalActions,
  PageHead,
  PageHeadRow,
  StatusPill,
  useAction,
  useLoad,
} from '../ui';
import { usePlainLanguage } from '../plainLanguage';
import { ProvidersSection } from './Providers';
import { UsersSection } from './Users';

type Tab = 'outlets' | 'providers' | 'users' | 'activity';

/**
 * One organization, with its outlets, payment providers and users behind tabs.
 *
 * The tabs are real routes rather than component state so the back button and a
 * pasted link both land where the person expects.
 */
export function OrganizationDetail({ user }: { user: SessionUser }) {
  const params = useParams();
  const orgId = params.orgId ?? '';
  const tab = (params.tab as Tab | undefined) ?? 'outlets';
  const isStaff = user.role === 'staff';
  const plain = usePlainLanguage();

  const org = useLoad(() => api.organizations.get(orgId), [orgId]);

  return (
    <>
      {isStaff && (
        <Breadcrumbs
          trail={[
            { label: 'Organizations', to: '/organizations' },
            { label: org.data?.name ?? '…' },
          ]}
        />
      )}

      <PageHead
        title={org.data?.name ?? 'Organization'}
        hint={org.data ? undefined : 'Loading…'}
        actions={org.data ? <StatusPill status={org.data.status} /> : undefined}
      />

      <ErrorBanner message={org.error} />

      <nav className="mb-5 flex gap-1 overflow-x-auto border-b border-divider">
        <TabLink
          to={`/organizations/${orgId}/outlets`}
          label={plain ? 'Shops' : 'Outlets'}
          active={tab === 'outlets'}
        />
        <TabLink
          to={`/organizations/${orgId}/providers`}
          label={plain ? 'Bank connection' : 'Payment providers'}
          active={tab === 'providers'}
        />
        <TabLink to={`/organizations/${orgId}/users`} label="Users" active={tab === 'users'} />
        <TabLink
          to={`/organizations/${orgId}/activity`}
          label="Activity"
          active={tab === 'activity'}
        />
      </nav>

      {tab === 'outlets' && <OutletsSection orgId={orgId} />}
      {tab === 'providers' && <ProvidersSection orgId={orgId} />}
      {tab === 'users' && <UsersSection user={user} orgId={orgId} />}
      {tab === 'activity' && <ActivityFeed orgId={orgId} />}
    </>
  );
}

function TabLink({ to, label, active }: { to: string; label: string; active: boolean }) {
  return (
    <NavLink
      to={to}
      className={`border-b-2 px-3.5 py-2.5 whitespace-nowrap no-underline ${
        active ? 'border-accent text-text' : 'border-transparent text-muted hover:text-text'
      }`}
    >
      {label}
    </NavLink>
  );
}

/** The outlets belonging to one organization — never a flat list across customers. */
function OutletsSection({ orgId }: { orgId: string }) {
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const outlets = useLoad(() => api.outlets.list(orgId, showArchived), [orgId, showArchived]);
  const action = useAction();
  const plain = usePlainLanguage();
  const cascadeNote = plain
    ? 'Its counters stop taking mobile payments.'
    : 'Its counters are archived with it and stop taking payments.';

  return (
    <>
      <div className="mb-3.5">
        <PageHeadRow
          actions={
            <>
              <ArchivedToggle showArchived={showArchived} onChange={setShowArchived} />
              <Button onClick={() => setCreating(true)}>
                {plain ? 'Add a shop' : 'New outlet'}
              </Button>
            </>
          }
        >
          <p className="m-0 max-w-[70ch] text-muted">
            {plain
              ? 'Open a shop to see its counters and connect a new one.'
              : 'An outlet is a shop. Open one to manage its counters.'}
          </p>
        </PageHeadRow>
      </div>

      <ErrorBanner message={outlets.error ?? action.error} />

      <Modal
        open={creating}
        title={plain ? 'Add a shop' : 'Add an outlet'}
        onClose={() => setCreating(false)}
      >
        <NewOutlet
          orgId={orgId}
          onCreated={() => {
            setCreating(false);
            outlets.reload();
          }}
        />
      </Modal>

      {outlets.loading && <Empty>Loading…</Empty>}
      {!outlets.loading && outlets.data?.length === 0 && (
        <Card>
          <Empty>
            {plain
              ? showArchived
                ? 'No shops, archived or otherwise.'
                : 'No shops yet.'
              : showArchived
                ? 'No outlets, archived or otherwise.'
                : 'No outlets yet.'}
          </Empty>
        </Card>
      )}

      {outlets.data && outlets.data.length > 0 && (
        <List>
          {outlets.data.map((outlet) => (
            <ListRow
              key={outlet.id}
              archived={outlet.status === 'archived'}
              title={
                <>
                  <Link
                    to={`/organizations/${orgId}/outlets/${outlet.id}`}
                    className="text-accent"
                  >
                    {outlet.name}
                  </Link>
                  <StatusPill status={outlet.status} />
                </>
              }
              subtitle={
                <>
                  {outlet._count
                    ? `${outlet._count.counters} counter${outlet._count.counters === 1 ? '' : 's'}`
                    : 'Counters'}
                  {/* Every shop is on Maldives time; only staff need to see it said. */}
                  {!plain && ` · ${outlet.timezone}`}
                </>
              }
              actions={
                <ArchiveButton
                  status={outlet.status}
                  label={outlet.name}
                  cascadeNote={cascadeNote}
                  busy={action.busy}
                  onArchive={() =>
                    void action.run(() => api.outlets.archive(outlet.id)).then(outlets.reload)
                  }
                  onRestore={() =>
                    void action.run(() => api.outlets.restore(outlet.id)).then(outlets.reload)
                  }
                />
              }
            />
          ))}
        </List>
      )}
    </>
  );
}

/**
 * Creating an outlet asks for a name and nothing else. The MPOS shop id used to sit
 * here, but nothing reads it yet, so it belongs on the outlet's own page as something
 * to fill in when it matters rather than a question at setup time.
 */
function NewOutlet({ orgId, onCreated }: { orgId: string; onCreated: () => void }) {
  const [name, setName] = useState('');
  const action = useAction();
  const plain = usePlainLanguage();

  async function create(event: FormEvent) {
    event.preventDefault();
    const ok = await action.run(() => api.outlets.create({ orgId, name: name.trim() }));
    if (ok) {
      setName('');
      onCreated();
    }
  }

  return (
    <form onSubmit={create} className="flex flex-col gap-3">
      <ErrorBanner message={action.error} />
      <Field label={plain ? 'Shop name' : 'Outlet name'}>
        <input
          required
          autoFocus
          value={name}
          placeholder="Main Shop"
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <ModalActions>
        <Button type="submit" disabled={action.busy}>
          {action.busy ? 'Adding…' : plain ? 'Add shop' : 'Add outlet'}
        </Button>
      </ModalActions>
    </form>
  );
}
